
import type { PropType, SetupContext } from 'vue'
import type {
  HighlightsFilterSetup,
  RegularFilter,
  RegularFilterSelectedValues,
} from '../../models'
import { computed, defineComponent, ref, watch } from 'vue'
import EpbAside from '../aside.component.vue'
import { UiAlert } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import RegularFilterSelector from './regular-filter-selector.component.vue'

export default defineComponent({
  name: 'FiltersAside',
  components: {
    EpbAside,
    UiAlert,
    RegularFilterSelector,
    EpbButton,
  },
  props: {
    filters: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    excludedFilters: {
      type: Array as PropType<Array<HighlightsFilterSetup>>,
      default: () => [],
    },
    openButtonHidden: {
      type: Boolean,
      default: false,
    },
    showAcceptIcon: {
      type: Boolean,
      default: true,
    },
    startOpened: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object, // { title: string; subtitle?: string; accept: string; }
      required: true,
    },
    isExtraContentUpdated: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: Record<string, any>, ctx: SetupContext) {
    const { emit } = ctx

    const baseClass = 'epb-filters-aside'

    const opened = ref(props.startOpened)
    const hideUiAlert = ref(false)

    const formFilters = ref({ ...props.filters } as Record<string, RegularFilter>)

    watch(
      () => props.filters,
      () => {
        formFilters.value = { ...props.filters }
      },
      { deep: true },
    )

    const visibleFormFilters = computed(() =>
      Object.fromEntries(
        Object.values(formFilters.value)
          .filter(filter => filter.config.values.length > 1)
          .map(filter => [filter.config.id, filter]),
      ),
    )

    const formFiltersSelectedValues = computed(() =>
      Object.fromEntries(
        Object.values(formFilters.value).map(filter => [filter.config.id, filter.selectedValues]),
      ),
    )

    const onFormFilterSelectedValuesChange = (
      filter: RegularFilter,
      selectedValues: RegularFilterSelectedValues,
    ): void => {
      formFilters.value = {
        ...formFilters.value,
        [filter.config.id]: {
          ...formFilters.value[filter.config.id],
          selectedValues,
        },
      }
    }

    const formIsCompleted = computed((): boolean => {
      return Object.values(formFilters.value).every(
        filter => !filter.config.mandatory || !!filter.selectedValues.length,
      )
    })

    const formNotUpdated = computed((): boolean => {
      return (
        JSON.stringify(props.filters) === JSON.stringify(formFilters.value) &&
        !props.isExtraContentUpdated
      )
    })

    const formHasInvalidFilters = computed((): boolean => {
      if (!formIsCompleted.value || formNotUpdated.value) {
        return false
      }

      return props.excludedFilters.some((filterNotAvailable: HighlightsFilterSetup): boolean => {
        return Object.keys(formFilters.value).every((filterKey: string): boolean => {
          const valuesSelected = formFilters.value[filterKey].selectedValues
          const valuesNotAvailable = filterNotAvailable.filters[filterKey]
          if (valuesSelected.length !== valuesNotAvailable?.length) {
            return false
          }

          return Object.values(valuesSelected).toString() === valuesNotAvailable.toString()
        })
      })
    })

    const isApplyButtonDisabled = computed(
      (): boolean => !formIsCompleted.value || formNotUpdated.value || formHasInvalidFilters.value,
    )

    const open = (): void => {
      opened.value = true
    }

    const close = (): void => {
      opened.value = false
      emit('close')
    }

    const accept = (): void => {
      hideUiAlert.value = true

      emit('accept', formFiltersSelectedValues.value)
      close()
    }

    return {
      baseClass,
      opened,
      formFilters,
      visibleFormFilters,
      formHasInvalidFilters,
      hideUiAlert,
      isApplyButtonDisabled,
      open,
      close,
      onFormFilterSelectedValuesChange,
      accept,
    }
  },
})
